import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import './ProductManagement.css';
import {
  Package,
  Search,
  LayoutGrid,
  List,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  Tag,
  Box,
  Calendar,
  X,
  ChevronRight,
  Plus,
  RefreshCw,
  XCircle,
  Store,
  Users,
  CheckCircle,
  XOctagon,
  Star,
  Archive,
  MoreVertical
} from 'lucide-react';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // View state
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [merchantFilter, setMerchantFilter] = useState('');

  // Bulk selection
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [productToFeature, setProductToFeature] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const productsPerPage = 50;

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Build query params
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: productsPerPage.toString()
      });

      if (statusFilter && statusFilter !== 'ALL') params.append('status', statusFilter);
      if (merchantFilter) params.append('merchantId', merchantFilter);
      if (searchTerm) params.append('search', searchTerm);

      console.log('Fetching products with params:', params.toString());
      const response = await api.get(`/admin/products?${params.toString()}`);

      console.log('Products API response:', response);

      if (response.success && response.products) {
        setProducts(response.products);
        setTotalProducts(response.total || response.products.length);
        console.log('Products loaded:', response.products.length);
      } else if (response.data && response.data.products) {
        setProducts(response.data.products);
        setTotalProducts(response.data.total || response.data.products.length);
      } else {
        setProducts([]);
        setTotalProducts(0);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products: ' + err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, merchantFilter, searchTerm]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleViewDetails = async (productId) => {
    try {
      setError('');
      console.log('Fetching product details for:', productId);
      const response = await api.get(`/admin/products/${productId}`);

      console.log('Product details response:', response);

      if (response.success && response.product) {
        setSelectedProduct(response.product);
        setShowDetailsModal(true);
      } else if (response.data && response.data.product) {
        setSelectedProduct(response.data.product);
        setShowDetailsModal(true);
      } else {
        setError('Failed to load product details');
      }
    } catch (err) {
      console.error('Error fetching product details:', err);
      setError('Failed to load product details: ' + err.message);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProducts();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Admin Actions
  const handleApproveProduct = async (productId) => {
    try {
      setError('');
      const response = await api.post(`/admin/products/${productId}/approve`);
      if (response.success) {
        fetchProducts();
      } else {
        setError('Failed to approve product');
      }
    } catch (err) {
      console.error('Error approving product:', err);
      setError('Failed to approve product: ' + err.message);
    }
  };

  const handleRejectProduct = async (productId) => {
    try {
      setError('');
      const response = await api.post(`/admin/products/${productId}/reject`);
      if (response.success) {
        fetchProducts();
      } else {
        setError('Failed to reject product');
      }
    } catch (err) {
      console.error('Error rejecting product:', err);
      setError('Failed to reject product: ' + err.message);
    }
  };

  const handleToggleFeature = async (productId, currentFeaturedStatus) => {
    try {
      setError('');
      const response = await api.post(`/admin/products/${productId}/feature`, {
        featured: !currentFeaturedStatus
      });
      if (response.success) {
        fetchProducts();
        setShowFeatureModal(false);
        setProductToFeature(null);
      } else {
        setError('Failed to update product featured status');
      }
    } catch (err) {
      console.error('Error updating featured status:', err);
      setError('Failed to update featured status: ' + err.message);
    }
  };

  const handleArchiveProduct = async (productId) => {
    try {
      setError('');
      const response = await api.post(`/admin/products/${productId}/archive`);
      if (response.success) {
        fetchProducts();
      } else {
        setError('Failed to archive product');
      }
    } catch (err) {
      console.error('Error archiving product:', err);
      setError('Failed to archive product: ' + err.message);
    }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      setError('');
      const response = await api.delete(`/admin/products/${productToDelete.id}`);
      if (response.success) {
        fetchProducts();
        setShowDeleteModal(false);
        setProductToDelete(null);
      } else {
        setError('Failed to delete product');
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product: ' + err.message);
    }
  };

  // Bulk Actions
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      }
      return [...prev, productId];
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const handleBulkApprove = async () => {
    try {
      setError('');
      const response = await api.post('/admin/products/bulk-approve', {
        productIds: selectedProducts
      });
      if (response.success) {
        setSelectedProducts([]);
        fetchProducts();
      } else {
        setError('Failed to approve products');
      }
    } catch (err) {
      console.error('Error bulk approving:', err);
      setError('Failed to bulk approve: ' + err.message);
    }
  };

  const handleBulkReject = async () => {
    try {
      setError('');
      const response = await api.post('/admin/products/bulk-reject', {
        productIds: selectedProducts
      });
      if (response.success) {
        setSelectedProducts([]);
        fetchProducts();
      } else {
        setError('Failed to reject products');
      }
    } catch (err) {
      console.error('Error bulk rejecting:', err);
      setError('Failed to bulk reject: ' + err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedProducts.length} products?`)) return;

    try {
      setError('');
      const response = await api.post('/admin/products/bulk-delete', {
        productIds: selectedProducts
      });
      if (response.success) {
        setSelectedProducts([]);
        fetchProducts();
      } else {
        setError('Failed to delete products');
      }
    } catch (err) {
      console.error('Error bulk deleting:', err);
      setError('Failed to bulk delete: ' + err.message);
    }
  };

  // Show bulk actions when products are selected
  useEffect(() => {
    setShowBulkActions(selectedProducts.length > 0);
  }, [selectedProducts]);

  const formatPrice = (price, currency = 'NGN') => {
    if (!price) return 'N/A';
    try {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: currency || 'NGN'
      }).format(price);
    } catch (error) {
      return `${currency || 'NGN'} ${parseFloat(price).toLocaleString()}`;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'status-badge active';
      case 'DRAFT': return 'status-badge draft';
      case 'INACTIVE': return 'status-badge inactive';
      case 'ARCHIVED': return 'status-badge archived';
      default: return 'status-badge';
    }
  };

  const totalPages = Math.ceil(totalProducts / productsPerPage);

  if (loading && products.length === 0) {
    return (
      <div className="products-page">
        <div className="products-loading">
          <div className="loading-spinner"></div>
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      {/* Header */}
      <div className="products-header">
        <h1>
          <Package size={24} /> Product Management
        </h1>
        <div className="products-header-actions">
          <button
            className="btn btn-refresh"
            onClick={() => {
              setCurrentPage(1);
              fetchProducts();
            }}
            disabled={loading}
          >
            <RefreshCw size={18} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Status Filters */}
      <div className="products-filters">
        <div className="status-filter-buttons">
          {['ALL', 'DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED'].map(status => (
            <button
              key={status}
              className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
              onClick={() => {
                setStatusFilter(status);
                setCurrentPage(1);
              }}
            >
              {status === 'ALL' ? 'All Products' : status.charAt(0) + status.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="products-search-bar">
        <Search size={20} className="search-icon" />
        <input
          type="text"
          placeholder="Search by name, SKU, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="products-search-input"
        />
        {searchTerm && (
          <button
            type="button"
            className="search-clear-btn"
            onClick={handleClearSearch}
          >
            <X size={14} />
          </button>
        )}
        <button type="submit" className="search-submit-btn" disabled={loading}>
          Search
        </button>
      </form>

      {/* Bulk Actions Toolbar */}
      {showBulkActions && (
        <div className="bulk-actions-toolbar">
          <div className="bulk-selection-info">
            <input
              type="checkbox"
              checked={selectedProducts.length === products.length}
              onChange={handleSelectAll}
              className="bulk-checkbox"
            />
            <span>{selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-action-buttons">
            <button className="btn btn-sm btn-success" onClick={handleBulkApprove}>
              <CheckCircle size={14} /> Activate
            </button>
            <button className="btn btn-sm btn-warning" onClick={handleBulkReject}>
              <XOctagon size={14} /> Deactivate
            </button>
            <button className="btn btn-sm btn-danger" onClick={handleBulkDelete}>
              <Trash2 size={14} /> Delete
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setSelectedProducts([])}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* List Header with View Toggle */}
      <div className="products-list-header">
        <div>
          <h2>All Products</h2>
          <span className="products-count">
            {totalProducts} product{totalProducts !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="header-actions">
          {!showBulkActions && products.length > 0 && (
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={selectedProducts.length === products.length && products.length > 0}
                onChange={handleSelectAll}
                className="bulk-checkbox"
              />
              <span>Select All</span>
            </label>
          )}
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Products Grid/List */}
      {products.length === 0 ? (
        <div className="products-empty">
          <Package size={64} />
          <h3>No products found</h3>
          <p>
            {searchTerm || statusFilter
              ? 'Try adjusting your search or filters'
              : 'No products available at the moment'}
          </p>
          {(searchTerm || statusFilter) && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setCurrentPage(1);
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'products-grid' : 'products-list'}>
          {products.map(product => (
            <div
              key={product.id}
              className={`product-card ${selectedProducts.includes(product.id) ? 'selected' : ''}`}
            >
              {/* Checkbox for Bulk Selection */}
              <div className="product-checkbox" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedProducts.includes(product.id)}
                  onChange={() => handleSelectProduct(product.id)}
                  className="bulk-checkbox"
                />
              </div>

              {/* Card Header */}
              <div className="product-card-header" onClick={() => handleViewDetails(product.id)}>
                <div className="product-card-title">
                  <Package size={18} />
                  <div>
                    <h3>{product.name}</h3>
                    {product.sku && <p>SKU: {product.sku}</p>}
                  </div>
                </div>
                {product.primaryImage && (
                  <img
                    src={product.primaryImage}
                    alt={product.name}
                    className="product-card-image"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>

              {/* Card Badges */}
              <div className="product-card-badges">
                <span className={getStatusBadgeClass(product.status)}>
                  {product.status || 'DRAFT'}
                </span>
                <span className="category-badge">
                  <Tag size={12} /> {product.category || 'General'}
                </span>
                {product.quantity === 0 && (
                  <span className="stock-badge out-of-stock">Out of Stock</span>
                )}
              </div>

              {/* Card Metrics */}
              <div className="product-card-metrics">
                <div className="metric">
                  <DollarSign size={16} />
                  <div>
                    <div className="metric-value">{formatPrice(product.price)}</div>
                    <div className="metric-label">Price</div>
                  </div>
                </div>
                <div className="metric">
                  <Box size={16} />
                  <div>
                    <div className="metric-value">{product.quantity || 0}</div>
                    <div className="metric-label">Stock</div>
                  </div>
                </div>
                <div className="metric">
                  <Store size={16} />
                  <div>
                    <div className="metric-value">{product.merchant?.username || 'N/A'}</div>
                    <div className="metric-label">Merchant</div>
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div className="product-card-footer">
                <div className="product-card-date">
                  <Calendar size={14} />
                  {new Date(product.createdAt).toLocaleDateString()}
                </div>
                <div className="product-card-actions" onClick={(e) => e.stopPropagation()}>
                  {/* View Button */}
                  <button
                    className="btn-sm btn-view"
                    onClick={() => handleViewDetails(product.id)}
                    title="View details"
                  >
                    <Eye size={14} />
                  </button>

                  {/* Approve/Deactivate Buttons for DRAFT products */}
                  {product.status === 'DRAFT' && (
                    <>
                      <button
                        className="btn-sm btn-success"
                        onClick={() => handleApproveProduct(product.id)}
                        title="Activate product"
                      >
                        <CheckCircle size={14} />
                      </button>
                      <button
                        className="btn-sm btn-warning"
                        onClick={() => handleRejectProduct(product.id)}
                        title="Mark as inactive"
                      >
                        <XOctagon size={14} />
                      </button>
                    </>
                  )}

                  {/* Feature/Unfeature Button */}
                  <button
                    className={`btn-sm ${product.featured ? 'btn-featured' : 'btn-secondary'}`}
                    onClick={() => {
                      setProductToFeature(product);
                      setShowFeatureModal(true);
                    }}
                    title={product.featured ? 'Unfeature product' : 'Feature product'}
                  >
                    <Star size={14} fill={product.featured ? 'currentColor' : 'none'} />
                  </button>

                  {/* Archive Button for ACTIVE products */}
                  {product.status === 'ACTIVE' && (
                    <button
                      className="btn-sm btn-secondary"
                      onClick={() => handleArchiveProduct(product.id)}
                      title="Archive product"
                    >
                      <Archive size={14} />
                    </button>
                  )}

                  {/* Delete Button */}
                  <button
                    className="btn-sm btn-danger"
                    onClick={() => {
                      setProductToDelete(product);
                      setShowDeleteModal(true);
                    }}
                    title="Delete product"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="products-pagination">
          <div className="pagination-info">
            Showing {((currentPage - 1) * productsPerPage) + 1} to {Math.min(currentPage * productsPerPage, totalProducts)} of {totalProducts} products
          </div>
          <div className="pagination-controls">
            <button
              className="btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
            >
              <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
              Previous
            </button>
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`btn page-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className="btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || loading}
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && productToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Trash2 size={20} /> Confirm Delete</h2>
              <button
                className="btn-icon"
                onClick={() => setShowDeleteModal(false)}
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{productToDelete.name}</strong>?</p>
              <p className="text-danger">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setProductToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteProduct}
              >
                <Trash2 size={16} /> Delete Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feature Confirmation Modal */}
      {showFeatureModal && productToFeature && (
        <div className="modal-overlay" onClick={() => setShowFeatureModal(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Star size={20} /> {productToFeature.featured ? 'Unfeature' : 'Feature'} Product</h2>
              <button
                className="btn-icon"
                onClick={() => setShowFeatureModal(false)}
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>
                {productToFeature.featured
                  ? `Remove "${productToFeature.name}" from featured products?`
                  : `Add "${productToFeature.name}" to featured products?`}
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowFeatureModal(false);
                  setProductToFeature(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleToggleFeature(productToFeature.id, productToFeature.featured)}
              >
                <Star size={16} /> {productToFeature.featured ? 'Unfeature' : 'Feature'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      {showDetailsModal && selectedProduct && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal product-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Package size={20} /> Product Details</h2>
              <button
                className="btn-icon"
                onClick={() => setShowDetailsModal(false)}
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Product Image */}
              {selectedProduct.primaryImage && (
                <div className="product-image-section">
                  <img
                    src={selectedProduct.primaryImage}
                    alt={selectedProduct.name}
                    className="product-detail-image"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              {/* Basic Info */}
              <div className="detail-section">
                <h3>Basic Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Product Name:</label>
                    <p>{selectedProduct.name}</p>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <p><span className={getStatusBadgeClass(selectedProduct.status)}>{selectedProduct.status}</span></p>
                  </div>
                  <div className="detail-item">
                    <label>Category:</label>
                    <p>{selectedProduct.category || 'General'}</p>
                  </div>
                  {selectedProduct.subcategory && (
                    <div className="detail-item">
                      <label>Subcategory:</label>
                      <p>{selectedProduct.subcategory}</p>
                    </div>
                  )}
                  {selectedProduct.brand && (
                    <div className="detail-item">
                      <label>Brand:</label>
                      <p>{selectedProduct.brand}</p>
                    </div>
                  )}
                  {selectedProduct.productType && (
                    <div className="detail-item">
                      <label>Product Type:</label>
                      <p>{selectedProduct.productType}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedProduct.description && (
                <div className="detail-section">
                  <h3>Description</h3>
                  <p className="description-text">{selectedProduct.description}</p>
                </div>
              )}

              {/* Pricing */}
              <div className="detail-section">
                <h3><DollarSign size={18} /> Pricing</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Price:</label>
                    <p className="price-highlight">{formatPrice(selectedProduct.price)}</p>
                  </div>
                  {selectedProduct.compareAtPrice && (
                    <div className="detail-item">
                      <label>Compare at Price:</label>
                      <p>{formatPrice(selectedProduct.compareAtPrice)}</p>
                    </div>
                  )}
                  {selectedProduct.costPrice && (
                    <div className="detail-item">
                      <label>Cost Price:</label>
                      <p>{formatPrice(selectedProduct.costPrice)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Inventory */}
              <div className="detail-section">
                <h3><Box size={18} /> Inventory</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Quantity:</label>
                    <p className={selectedProduct.quantity === 0 ? 'text-danger' : ''}>
                      {selectedProduct.quantity || 0} {selectedProduct.quantity === 0 ? '(Out of Stock)' : ''}
                    </p>
                  </div>
                  {selectedProduct.sku && (
                    <div className="detail-item">
                      <label>SKU:</label>
                      <p>{selectedProduct.sku}</p>
                    </div>
                  )}
                  {selectedProduct.barcode && (
                    <div className="detail-item">
                      <label>Barcode:</label>
                      <p>{selectedProduct.barcode}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Merchant Info */}
              {selectedProduct.merchant && (
                <div className="detail-section">
                  <h3><Users size={18} /> Merchant Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Merchant Name:</label>
                      <p>{selectedProduct.merchant.username}</p>
                    </div>
                    <div className="detail-item">
                      <label>Email:</label>
                      <p>{selectedProduct.merchant.email}</p>
                    </div>
                    {selectedProduct.merchant.store && (
                      <>
                        <div className="detail-item">
                          <label>Store Name:</label>
                          <p>{selectedProduct.merchant.store.name}</p>
                        </div>
                        {selectedProduct.merchant.store.slug && (
                          <div className="detail-item">
                            <label>Store URL:</label>
                            <p>{selectedProduct.merchant.store.slug}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Physical Attributes */}
              {(selectedProduct.weight || selectedProduct.dimensions || selectedProduct.material || selectedProduct.color || selectedProduct.size) && (
                <div className="detail-section">
                  <h3>Physical Attributes</h3>
                  <div className="detail-grid">
                    {selectedProduct.weight && (
                      <div className="detail-item">
                        <label>Weight:</label>
                        <p>{selectedProduct.weight} kg</p>
                      </div>
                    )}
                    {selectedProduct.dimensions && Object.keys(selectedProduct.dimensions).length > 0 && (
                      <div className="detail-item">
                        <label>Dimensions:</label>
                        <p>
                          {selectedProduct.dimensions.length && `L: ${selectedProduct.dimensions.length}cm`}
                          {selectedProduct.dimensions.width && ` × W: ${selectedProduct.dimensions.width}cm`}
                          {selectedProduct.dimensions.height && ` × H: ${selectedProduct.dimensions.height}cm`}
                        </p>
                      </div>
                    )}
                    {selectedProduct.material && (
                      <div className="detail-item">
                        <label>Material:</label>
                        <p>{selectedProduct.material}</p>
                      </div>
                    )}
                    {selectedProduct.color && (
                      <div className="detail-item">
                        <label>Color:</label>
                        <p>{selectedProduct.color}</p>
                      </div>
                    )}
                    {selectedProduct.size && (
                      <div className="detail-item">
                        <label>Size:</label>
                        <p>{selectedProduct.size}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {selectedProduct.tags && selectedProduct.tags.length > 0 && (
                <div className="detail-section">
                  <h3><Tag size={18} /> Tags</h3>
                  <div className="tags-list">
                    {selectedProduct.tags.map((tag, index) => (
                      <span key={index} className="tag-badge">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Gallery */}
              {selectedProduct.gallery && selectedProduct.gallery.length > 0 && (
                <div className="detail-section">
                  <h3>Gallery</h3>
                  <div className="gallery-grid">
                    {selectedProduct.gallery.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`${selectedProduct.name} ${index + 1}`}
                        className="gallery-image"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="detail-section">
                <h3>Metadata</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Product ID:</label>
                    <p className="code-text">{selectedProduct.id}</p>
                  </div>
                  <div className="detail-item">
                    <label>Slug:</label>
                    <p className="code-text">{selectedProduct.slug}</p>
                  </div>
                  <div className="detail-item">
                    <label>Created At:</label>
                    <p>{new Date(selectedProduct.createdAt).toLocaleString()}</p>
                  </div>
                  <div className="detail-item">
                    <label>Updated At:</label>
                    <p>{new Date(selectedProduct.updatedAt).toLocaleString()}</p>
                  </div>
                  {selectedProduct.featured && (
                    <div className="detail-item">
                      <label>Featured:</label>
                      <p>Yes</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
